import {
  Comparison,
  Condition,
  ConditionExpression,
  ConditionGroup,
  ConditionGroupOperator,
  ConditionValueKind,
  NestedConditionExpression,
} from '@repo/sdk/models/components';

import { getRefValue } from '@/features/equipment-browser/utils';

import { conditionGroupHandlers, performComparison } from './comparison-handler';
import { DataLookupService } from './types';
/**
 * Retrieves the source value based on the ref configuration
 * If the ref is not available, returns undefined
 */
const getSourceValue = (condition: Condition): any => {
  const { ref } = condition;

  return ref ? getRefValue(ref) : undefined;
};

/**
 * Extracts the target value from ConditionValue based on its kind
 */
export const getTargetValue = (condition: Condition): any => {
  const { value } = condition;

  switch (value.kind) {
    case ConditionValueKind.Boolean:
      return value.boolean;

    case ConditionValueKind.Number:
      return value.number;

    case ConditionValueKind.Enum:
      return value.enum;

    case ConditionValueKind.Schedule:
      return value.schedule;

    case ConditionValueKind.Sequence:
      return value.sequence;

    case ConditionValueKind.String:
      return value.string;

    case ConditionValueKind.EnumArray:
      return value.enumArray;

    default:
      console.warn(`Unknown value kind: ${value.kind}`);
      return null;
  }
};

// helper function to compute the result of a condition
const computeConditionResult = (condition: Condition): boolean => {
  try {
    // Get the source value using the ref
    const sourceValue = getSourceValue(condition);
    if (sourceValue === null || sourceValue === undefined) {
      // isUnderEaiControl comparison doesn't need a source value otherwise the condition evaluates to false
      if (condition.comparison !== Comparison.IsUnderEaiControl) {
        return false;
      }
    }

    // Get the target value from the condition
    const targetValue = getTargetValue(condition);
    if (targetValue === null || targetValue === undefined) {
      // Some comparisons don't need target values (e.g., isNilOrEmpty)
      const nilComparisons = [Comparison.IsNilOrEmpty, Comparison.IsNotNilOrEmpty] as const;
      const needsTargetValue = !nilComparisons.includes(condition.comparison as any);

      if (needsTargetValue) {
        return false;
      }
    }

    // Resolve secondary ref if present (used by IsTrimAtCurrentStage)
    const secondaryValue = condition.secondaryRef ? getRefValue(condition.secondaryRef) : undefined;

    // Perform the comparison based on the comparison type
    const comparisonResult = performComparison(condition, sourceValue, targetValue, secondaryValue);
    return comparisonResult;
  } catch (error) {
    console.error('Error evaluating condition:', error);
    return false;
  }
};

/**
 * Main condition evaluation function
 * @param condition - The condition to evaluate
 * @returns boolean result of the condition evaluation
 */
export const evaluateCondition = (condition: Condition): boolean => {
  const result = computeConditionResult(condition);
  // Apply negate logic (invert result if negate is true)
  return condition.negate === false ? result : !result;
};

/**
 * Helper function to create a mock data service for testing/development
 */
export const createMockDataService = (
  settings: Record<string, any> = {},
  readings: Record<string, any> = {}
): DataLookupService => {
  return {
    getSetting: (id: string) => {
      const value = settings[id];
      return value !== undefined ? { id, value } : null;
    },
    getReading: (id: string) => {
      const value = readings[id];
      return value !== undefined ? { id, value } : null;
    },
  };
};

/**
 * Type guard to check if a value is a valid condition
 */
export const isValidCondition = (condition: any): condition is Condition => {
  return (
    condition &&
    typeof condition === 'object' &&
    condition.comparison &&
    condition.ref &&
    condition.value
  );
};

/**
 * Evaluates a condition group
 */
export const evaluateConditionGroup = (conditionGroup: ConditionGroup) => {
  const conditions = conditionGroup.conditions;
  const operator: ConditionGroupOperator | undefined = conditionGroup.operator;
  const negate = conditionGroup.negate || false;

  if (!operator) {
    console.warn('No operator found in condition group');
    return false;
  }

  const handler = conditionGroupHandlers[operator];
  const result = handler(conditions.map((condition) => evaluateCondition(condition)));
  return negate ? !result : result;
};

/**
 * Evaluates a nested condition expression
 */
export const evaluateNestedConditionExpression = (
  nestedConditionExpression: NestedConditionExpression
) => {
  const conditionGroups = nestedConditionExpression.groups;
  const operator = nestedConditionExpression.operator;
  const negate = nestedConditionExpression.negate || false;

  if (!operator) {
    console.warn('No operator found in nested condition expression');
    return false;
  }

  const handler = conditionGroupHandlers[operator];
  const result = handler(
    conditionGroups.map((conditionGroup) => evaluateConditionGroup(conditionGroup))
  );
  return negate ? !result : result;
};

/**
 * Evaluates a nested condition expression
 */
const evaluateNestedExpression = (nestedExpression: NestedConditionExpression): boolean => {
  const { operator, groups, negate } = nestedExpression;

  if (!operator || !groups || groups.length === 0) {
    return false;
  }

  const results = groups.map((group) => {
    return evaluateConditionGroup(group);
  });
  let result = false;
  if (operator === 'logical_and') {
    result = results.every((r) => r);
  } else if (operator === 'logical_or') {
    result = results.some((r) => r);
  }

  return negate ? !result : result;
};

/**
 * Evaluates a condition expression (can be a single condition, condition group, or nested expression)
 */
export const evaluateConditionExpression = (
  expression: ConditionExpression | undefined
): boolean => {
  if (!expression) {
    return false;
  }

  if (expression.condition) {
    return evaluateCondition(expression.condition);
  }

  if (expression.conditionGroup) {
    return evaluateConditionGroup(expression.conditionGroup);
  }

  if (expression.nestedExpression) {
    return evaluateNestedExpression(expression.nestedExpression);
  }

  return false;
};
